import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import PropTypes from 'prop-types';
import callOracleApi from '../../utils/call-oracle-api.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import '../../styles/MissionRequests.css';

const TABS = ['pending', 'approved', 'rejected'];

const STATUS_LABELS = {
    pending: { label: 'Under Review', color: '#f39c12', bg: '#fef9e7' },
    approved: { label: 'Approved', color: '#27ae60', bg: '#eafaf1' },
    rejected: { label: 'Not Approved', color: '#c0392b', bg: '#fdedec' }
};

const MissionReviewPage = () => {
    const navigate = useNavigate();
    const { isLeadership, loading: userLoading } = useCurrentUser();
    const [activeTab, setActiveTab] = useState('pending');
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [editing, setEditing] = useState({});
    const [actionLoading, setActionLoading] = useState(null);

    const loadRequests = useCallback(async () => {
        setLoading(true);
        try {
            const response = await callOracleApi(
                `/mission-requests?status=${activeTab}`,
                'GET'
            );
            const data = await response.json();
            if (data.success) {
                setRequests(data.payload);
                setExpanded(null);
            } else {
                toast.error(data.message || 'Failed to load');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        if (!userLoading && isLeadership) loadRequests();
    }, [activeTab, isLeadership, userLoading, loadRequests]);

    if (userLoading)
        return (
            <div className="mr-page">
                <p>Loading...</p>
            </div>
        );

    if (!isLeadership) {
        return (
            <div
                className="mr-page"
                style={{ textAlign: 'center', paddingTop: '4rem' }}>
                <p style={{ color: '#888', marginBottom: '1rem' }}>
                    You don&apos;t have access to this page.
                </p>
                <button className="btn-primary" onClick={() => navigate('/')}>
                    Go Home
                </button>
            </div>
        );
    }

    const setEdit = (id, field, value) =>
        setEditing((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), [field]: value }
        }));

    const handleSaveEdits = async (req) => {
        setActionLoading(req.id);
        try {
            const updates = editing[req.id] || {};
            const response = await callOracleApi(
                `/mission-requests/${req.id}`,
                'PATCH',
                updates
            );
            const data = await response.json();
            if (data.success) {
                toast.success('Saved');
                setEditing((prev) => ({ ...prev, [req.id]: {} }));
                await loadRequests();
            } else {
                toast.error(data.message || 'Save failed');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setActionLoading(null);
        }
    };

    const handleApprove = async (req) => {
        const e = editing[req.id] || {};
        setActionLoading(req.id);
        try {
            const response = await callOracleApi(
                `/mission-requests/${req.id}/approve`,
                'PATCH',
                {
                    qProfitPoints:
                        e.qProfitPoints != null
                            ? Number(e.qProfitPoints)
                            : undefined,
                    leadershipNotes: e.leadershipNotes,
                    assignee_email: e.assignee_email,
                    due_date: e.due_date,
                    teamId: e.teamId
                }
            );
            const data = await response.json();
            if (data.success) {
                toast.success('Mission approved and published!');
                setEditing((prev) => ({ ...prev, [req.id]: {} }));
                await loadRequests();
            } else {
                toast.error(data.message || 'Approval failed');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (req) => {
        const e = editing[req.id] || {};
        if (!e.leadershipNotes?.trim()) {
            toast.error('Please add a note explaining the rejection');
            return;
        }
        setActionLoading(req.id);
        try {
            const response = await callOracleApi(
                `/mission-requests/${req.id}/reject`,
                'PATCH',
                { leadershipNotes: e.leadershipNotes }
            );
            const data = await response.json();
            if (data.success) {
                toast.success('Mission rejected');
                setEditing((prev) => ({ ...prev, [req.id]: {} }));
                await loadRequests();
            } else {
                toast.error(data.message || 'Rejection failed');
            }
        } catch {
            toast.error('Network error');
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="mr-page-wide">
            <div className="mr-page-header">
                <div>
                    <h1 className="mr-page-title">Mission Submission Review</h1>
                    <p className="mr-page-subtitle">
                        Review, edit, and publish mission requests from the team
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="mr-tabs">
                {TABS.map((tab) => (
                    <button
                        key={tab}
                        className={`mr-tab${
                            activeTab === tab ? ' active' : ''
                        }`}
                        onClick={() => setActiveTab(tab)}>
                        {tab === 'pending'
                            ? 'Pending Review'
                            : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {loading && <p style={{ color: '#888' }}>Loading...</p>}

            {!loading && requests.length === 0 && (
                <div
                    style={{
                        textAlign: 'center',
                        padding: '3rem',
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        color: '#888'
                    }}>
                    No {activeTab} submissions.
                </div>
            )}

            {!loading && requests.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem'
                    }}>
                    {requests.map((req) => {
                        const s =
                            STATUS_LABELS[req.status] || STATUS_LABELS.pending;
                        const isOpen = expanded === req.id;
                        const e = editing[req.id] || {};
                        const busy = actionLoading === req.id;

                        return (
                            <div key={req.id} className="mr-list-card">
                                {/* Card header */}
                                <div
                                    className="mr-card-header"
                                    onClick={() =>
                                        setExpanded(isOpen ? null : req.id)
                                    }>
                                    <div>
                                        <p className="mr-card-title">
                                            {req.title}
                                        </p>
                                        <p className="mr-card-meta">
                                            {req.submitterEmail}
                                            {' · '}
                                            {req.createdAt
                                                ? new Date(
                                                      req.createdAt
                                                  ).toLocaleDateString()
                                                : '—'}
                                        </p>
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10
                                        }}>
                                        <span
                                            className="mr-status-badge"
                                            style={{
                                                color: s.color,
                                                background: s.bg
                                            }}>
                                            {s.label}
                                        </span>
                                        <span
                                            style={{
                                                color: '#bbb',
                                                fontSize: '0.9rem'
                                            }}>
                                            {isOpen ? '▲' : '▼'}
                                        </span>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="mr-card-body">
                                        {/* Mission Info */}
                                        <div className="mr-detail-section">
                                            <p className="mr-detail-section-title">
                                                Mission Info
                                            </p>
                                            <div className="mr-detail-grid">
                                                <DetailItem
                                                    label="Problem to Solve"
                                                    value={req.problemStatement}
                                                    full
                                                />
                                                <DetailItem
                                                    label="Description"
                                                    value={req.description}
                                                    full
                                                />
                                                <DetailItem
                                                    label="Executive Objective"
                                                    value={
                                                        req.executiveObjective
                                                    }
                                                    full
                                                />
                                                <DetailItem
                                                    label="Estimated Weeks"
                                                    value={req.estimatedWeeks}
                                                />
                                                <DetailItem
                                                    label="Success Measure"
                                                    value={req.successMeasure}
                                                    full
                                                />
                                                <DetailItem
                                                    label="Estimated ROI"
                                                    value={req.estimatedRoi}
                                                    full
                                                />
                                            </div>
                                        </div>

                                        {/* Mission Team */}
                                        <div className="mr-detail-section">
                                            <p className="mr-detail-section-title">
                                                Mission Team
                                            </p>
                                            <div className="mr-detail-grid">
                                                <DetailItem
                                                    label="Potential Captain"
                                                    value={req.potentialCaptain}
                                                />
                                                <DetailItem
                                                    label="Interested Members"
                                                    value={
                                                        req.interestedMembers
                                                    }
                                                />
                                                <DetailItem
                                                    label="Departments Involved"
                                                    value={
                                                        req.involvedDepartments
                                                            ?.length
                                                            ? req.involvedDepartments.join(
                                                                  ', '
                                                              )
                                                            : '—'
                                                    }
                                                    full
                                                />
                                            </div>
                                        </div>

                                        {/* Development Needs */}
                                        <div className="mr-detail-section">
                                            <p className="mr-detail-section-title">
                                                Development Needs
                                            </p>
                                            <div className="mr-detail-grid">
                                                <DetailItem
                                                    label="Innovation Priority"
                                                    value={`${req.innovationPriority} / 5`}
                                                />
                                                <DetailItem
                                                    label="Hub Priority"
                                                    value={`${req.hubPriority} / 5`}
                                                />
                                                {req.innovationNeeds && (
                                                    <DetailItem
                                                        label="Innovation Needs"
                                                        value={
                                                            req.innovationNeeds
                                                        }
                                                        full
                                                    />
                                                )}
                                                {req.hubNeeds && (
                                                    <DetailItem
                                                        label="Hub Needs"
                                                        value={req.hubNeeds}
                                                        full
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Leadership Review Panel (pending only) */}
                                        {req.status === 'pending' && (
                                            <div className="mr-review-panel">
                                                <p className="mr-review-panel-title">
                                                    Leadership Review
                                                </p>
                                                <div className="mr-review-grid">
                                                    <div>
                                                        <label className="mr-field-label">
                                                            Q Profit Points
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-input"
                                                            value={
                                                                e.qProfitPoints ??
                                                                req.qProfitPoints ??
                                                                ''
                                                            }
                                                            onChange={(ev) =>
                                                                setEdit(
                                                                    req.id,
                                                                    'qProfitPoints',
                                                                    ev.target
                                                                        .value
                                                                )
                                                            }
                                                            placeholder="e.g. 50"
                                                            min={0}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="mr-field-label">
                                                            Assignee Email
                                                        </label>
                                                        <input
                                                            className="form-input"
                                                            value={
                                                                e.assignee_email ??
                                                                req.submitterEmail
                                                            }
                                                            onChange={(ev) =>
                                                                setEdit(
                                                                    req.id,
                                                                    'assignee_email',
                                                                    ev.target
                                                                        .value
                                                                )
                                                            }
                                                            placeholder={
                                                                req.submitterEmail
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="mr-field-label">
                                                            Due Date
                                                        </label>
                                                        <input
                                                            type="date"
                                                            className="form-input"
                                                            value={
                                                                e.due_date ?? ''
                                                            }
                                                            onChange={(ev) =>
                                                                setEdit(
                                                                    req.id,
                                                                    'due_date',
                                                                    ev.target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="mr-field-label">
                                                            Team (Oyster team
                                                            ID)
                                                        </label>
                                                        <input
                                                            className="form-input"
                                                            value={
                                                                e.teamId ?? ''
                                                            }
                                                            onChange={(ev) =>
                                                                setEdit(
                                                                    req.id,
                                                                    'teamId',
                                                                    ev.target
                                                                        .value
                                                                )
                                                            }
                                                            placeholder="e.g. Leadership"
                                                        />
                                                    </div>
                                                </div>

                                                <div
                                                    style={{
                                                        marginTop: '0.75rem'
                                                    }}>
                                                    <label className="mr-field-label">
                                                        Leadership Notes /
                                                        Feedback
                                                    </label>
                                                    <textarea
                                                        className="form-textarea"
                                                        rows={3}
                                                        value={
                                                            e.leadershipNotes ??
                                                            req.leadershipNotes ??
                                                            ''
                                                        }
                                                        onChange={(ev) =>
                                                            setEdit(
                                                                req.id,
                                                                'leadershipNotes',
                                                                ev.target.value
                                                            )
                                                        }
                                                        placeholder="Notes visible to the submitter..."
                                                    />
                                                </div>

                                                <div className="mr-review-actions">
                                                    <button
                                                        className="btn-secondary"
                                                        onClick={() =>
                                                            handleSaveEdits(req)
                                                        }
                                                        disabled={busy}>
                                                        {busy
                                                            ? '...'
                                                            : 'Save Draft'}
                                                    </button>
                                                    <button
                                                        className="btn-danger"
                                                        onClick={() =>
                                                            handleReject(req)
                                                        }
                                                        disabled={busy}>
                                                        {busy
                                                            ? '...'
                                                            : '✗ Reject'}
                                                    </button>
                                                    <button
                                                        className="btn-success"
                                                        onClick={() =>
                                                            handleApprove(req)
                                                        }
                                                        disabled={busy}>
                                                        {busy
                                                            ? '...'
                                                            : '✓ Approve & Publish'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Reviewed info */}
                                        {req.status !== 'pending' && (
                                            <div
                                                className="mr-feedback-box"
                                                style={{
                                                    borderColor: s.color,
                                                    background: s.bg
                                                }}>
                                                <p
                                                    className="mr-feedback-box-label"
                                                    style={{ color: s.color }}>
                                                    {req.status === 'approved'
                                                        ? 'Approved'
                                                        : 'Rejected'}{' '}
                                                    by {req.reviewedBy}
                                                    {req.reviewedAt
                                                        ? ` on ${new Date(
                                                              req.reviewedAt
                                                          ).toLocaleDateString()}`
                                                        : ''}
                                                </p>
                                                {req.qProfitPoints != null && (
                                                    <p
                                                        style={{
                                                            margin: '4px 0 0',
                                                            fontSize: '0.9rem'
                                                        }}>
                                                        <strong>
                                                            Q Profit Points:
                                                        </strong>{' '}
                                                        {req.qProfitPoints}
                                                    </p>
                                                )}
                                                {req.leadershipNotes && (
                                                    <p
                                                        style={{
                                                            margin: '4px 0 0',
                                                            fontSize: '0.9rem'
                                                        }}>
                                                        <strong>Notes:</strong>{' '}
                                                        {req.leadershipNotes}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const DetailItem = ({ label, value, full }) => (
    <div className={`mr-detail-item${full ? ' full' : ''}`}>
        <span className="mr-detail-label">{label}</span>
        <span className="mr-detail-value">{value || '—'}</span>
    </div>
);

DetailItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full: PropTypes.bool
};

export default MissionReviewPage;
